import React, {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router";
import {Button, Chip, CircularProgress, Typography} from "@mui/material";
import {AddOutlined, DeleteOutlineOutlined, SettingsOutlined} from "@mui/icons-material";

import {deleteQuiz, getQuiz, QuizState} from "../../data/quiz";
import TrainingSetupDialog from "./TrainingSetupDialog";
import {Grey2, Grey5, Grey17} from "../../theme/styles";

// The Quizzes tab: what training this revision requires, and the quiz that satisfies it.
//
// REACHED ONLY WHEN THE RECORD REQUIRES TRAINING -- the Detail tab's own switch decides that, which is
// why this tab can assume it and say nothing about it. Training Setup here is the three things that
// training TAKES (acknowledgment, retraining, a quiz), not whether it is needed at all.

export default function QuizzesTab({objectRevisionUSID, sessionContext, onChanged}: {
    objectRevisionUSID: string;
    sessionContext: any;
    onChanged?: () => void;
}) {
    const navigate = useNavigate();
    const [state, setState] = useState<QuizState | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [settingUp, setSettingUp] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        getQuiz(sessionContext, objectRevisionUSID)
            .then(result => { setState(result); setError(null); })
            .catch(problem => setError(problem?.response?.data?.Cause ?? "Could not read the quiz."))
            .finally(() => setLoading(false));
    }, [sessionContext, objectRevisionUSID]);

    useEffect(load, [load]);

    const remove = async () => {
        setBusy(true);
        try {
            await deleteQuiz(sessionContext, objectRevisionUSID);
            load();
        } catch (problem: any) {
            setError(problem?.response?.data?.Cause ?? "Could not remove the quiz.");
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <div style={{marginTop: 32}}><CircularProgress size={22}/></div>;
    }
    if (error && !state) {
        return <Typography style={{fontSize: 13, color: "#B42318", marginTop: 24}}>{error}</Typography>;
    }
    if (!state) return null;

    const quiz = state.Quiz;
    const editable = state.Editable;

    return (
        <div style={{marginTop: 24, maxWidth: 720, marginBottom: 40}}>
            {/* WHAT TRAINING THIS REVISION REQUIRES, said in words rather than in three switch
                positions somebody has to open a dialog to read. */}
            <div style={{border: `1px solid ${Grey2}`, borderRadius: 10, padding: 20}}>
                <div style={{display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                             gap: 16}}>
                    <div>
                        <Typography style={{fontSize: 16, color: Grey17}}>
                            People must be trained on this revision.
                        </Typography>
                        <div style={{display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap"}}>
                            {state.TrainingRequireAcknowledgment ? (
                                <Chip size="small" label="Must acknowledge" sx={{fontSize: 11, height: 22}}/>
                            ) : null}
                            {state.TrainingRequireRetraining ? (
                                <Chip size="small" label="Retrain on update" sx={{fontSize: 11, height: 22}}/>
                            ) : null}
                            {state.TrainingRequireQuiz ? (
                                <Chip size="small" label="Must pass a quiz" sx={{fontSize: 11, height: 22}}/>
                            ) : null}
                            <Chip size="small" variant="outlined"
                                  label={`Status: ${String(state.Status ?? "").replace("_", " ").toLowerCase()}`}
                                  sx={{fontSize: 11, height: 22}}/>
                        </div>
                    </div>
                    <Button size="small" variant="outlined" startIcon={<SettingsOutlined/>}
                            onClick={() => setSettingUp(true)}
                            sx={{textTransform: "none", whiteSpace: "nowrap"}}>
                        Training Setup
                    </Button>
                </div>
            </div>

            <div style={{marginTop: 20, border: `1px solid ${Grey2}`, borderRadius: 10, padding: 20}}>
                    {quiz ? (
                        <>
                            <Typography style={{fontSize: 16, color: Grey17}}>
                                {quiz.QuestionCount} {quiz.QuestionCount === 1 ? "question" : "questions"}
                                {" · pass at "}{quiz.PassingScore}%
                            </Typography>
                            <Typography style={{fontSize: 13, color: Grey5, marginTop: 6}}>
                                {quiz.ShuffleAnswers ? "Answers are shuffled for each taker. " : ""}
                                {quiz.AllowPartialCredit
                                    ? "A partly-right answer earns part of the mark."
                                    : "Every question is all or nothing."}
                            </Typography>
                            <div style={{display: "flex", gap: 8, marginTop: 16}}>
                                <Button size="small" variant="contained"
                                        onClick={() => navigate(`/quiz/${objectRevisionUSID}`)}
                                        sx={{textTransform: "none"}}>
                                    {editable ? "Open Quiz Builder" : "View the quiz"}
                                </Button>
                                {editable ? (
                                    <Button size="small" color="error" disabled={busy}
                                            startIcon={<DeleteOutlineOutlined/>} onClick={remove}
                                            sx={{textTransform: "none"}}>
                                        Remove
                                    </Button>
                                ) : null}
                            </div>
                            {!editable ? (
                                <Typography style={{fontSize: 13, color: Grey5, marginTop: 12}}>
                                    This revision is released, so its quiz is frozen — people were trained
                                    against these questions. Revise the record to change them.
                                </Typography>
                            ) : null}
                        </>
                    ) : (
                        <>
                            <Typography style={{fontSize: 16, color: Grey17}}>
                                No quiz yet.
                            </Typography>
                            <Typography style={{fontSize: 13, color: Grey5, marginTop: 6}}>
                                {state.TrainingRequireQuiz
                                    ? "This revision cannot be released until it has one."
                                    : "A quiz is optional here, because Required Quiz is off."}
                            </Typography>
                            {/* CREATE IS DRAFT-ONLY. The server refuses it outside a draft either way;
                                not offering it avoids proposing a change that would be refused. */}
                            {editable ? (
                                <Button size="small" variant="contained" startIcon={<AddOutlined/>}
                                        onClick={() => navigate(`/quiz/${objectRevisionUSID}`)}
                                        sx={{textTransform: "none", marginTop: 16}}>
                                    Create a quiz
                                </Button>
                            ) : (
                                <Typography style={{fontSize: 13, color: Grey5, marginTop: 12}}>
                                    A quiz is authored on a draft. Revise the record to add one.
                                </Typography>
                            )}
                        </>
                    )}
            </div>

            {error ? (
                <Typography style={{fontSize: 13, color: "#B42318", marginTop: 16}}>{error}</Typography>
            ) : null}

            <TrainingSetupDialog open={settingUp} onClose={() => setSettingUp(false)}
                                 sessionContext={sessionContext}
                                 objectRevisionUSID={objectRevisionUSID}
                                 editable={editable}
                                 current={{
                                     TrainingRequired: state.TrainingRequired,
                                     TrainingRequireAcknowledgment: state.TrainingRequireAcknowledgment,
                                     TrainingRequireRetraining: state.TrainingRequireRetraining,
                                     TrainingRequireQuiz: state.TrainingRequireQuiz,
                                 }}
                                 onSaved={() => { load(); onChanged?.(); }}/>
        </div>
    );
}
